import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  Mail, Users, Send, Eye, ArrowLeft, Plus, X, 
  CheckCircle2, XCircle, AlertCircle, Sparkles, Settings
} from 'lucide-react';
import { getLatestRanking, verifyEmailCredentials, sendEmails, previewEmail } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

function EmailPage() {
  const navigate = useNavigate();
  
  // Ranking data state
  const [rankingData, setRankingData] = useState(null);
  const [loadingRanking, setLoadingRanking] = useState(true);
  const [rankingError, setRankingError] = useState(null);
  
  // Credentials state
  const [credentials, setCredentials] = useState(null);
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  
  // Form state
  const [topN, setTopN] = useState(0);
  const [manualRecipients, setManualRecipients] = useState([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  
  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [error, setError] = useState(null);

  // Quill editor modules
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  }), []);

  // Load ranking data and credentials on mount
  useEffect(() => {
    loadRankingData();
    loadCredentials();
  }, []);

  const loadRankingData = async () => {
    try {
      setLoadingRanking(true);
      setRankingError(null);
      const data = await getLatestRanking();
      setRankingData(data);
    } catch (err) {
      setRankingError(err.response?.data?.error || 'Failed to load ranking data');
    } finally {
      setLoadingRanking(false);
    }
  };

  const loadCredentials = async () => {
    try {
      setLoadingCredentials(true);
      const data = await verifyEmailCredentials();
      setCredentials(data);
    } catch (err) {
      setCredentials({ configured: false, error: err.message });
    } finally {
      setLoadingCredentials(false);
    }
  };

  // Calculate candidates with emails in top N
  const candidatesWithEmails = useMemo(() => {
    if (!rankingData?.results) return 0;
    const topResults = rankingData.results.slice(0, topN || 0);
    return topResults.filter(r => r.email).length;
  }, [rankingData, topN]);

  // Add manual recipient
  const addManualRecipient = () => {
    if (!newRecipientEmail.trim()) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newRecipientEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (manualRecipients.some(r => r.email.toLowerCase() === newRecipientEmail.toLowerCase())) {
      setError('This email has already been added');
      return;
    }
    
    setManualRecipients([...manualRecipients, {
      email: newRecipientEmail.trim()
    }]);
    setNewRecipientEmail('');
    setError(null);
  };

  // Remove manual recipient
  const removeManualRecipient = (index) => {
    setManualRecipients(manualRecipients.filter((_, i) => i !== index));
  };

  // Handle preview
  const handlePreview = async () => {
    try {
      const data = await previewEmail(htmlContent);
      setPreviewHtml(data.preview_html);
      setShowPreview(true);
    } catch (err) {
      setError('Failed to generate preview');
    }
  };

  // Handle send
  const handleSend = async () => {
    if (!subject.trim()) {
      setError('Please enter an email subject');
      return;
    }
    if (!htmlContent.trim() || htmlContent === '<p><br></p>') {
      setError('Please enter email content');
      return;
    }
    if (topN === 0 && manualRecipients.length === 0) {
      setError('Please select top N candidates or add manual recipients');
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSendResult(null);
      
      const result = await sendEmails({
        top_n: topN,
        manual_recipients: manualRecipients,
        subject: subject,
        html_content: htmlContent
      });
      
      setSendResult(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  // Total recipients count
  const totalRecipients = candidatesWithEmails + manualRecipients.length;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center gap-3">
            <Sparkles className="h-8 w-8" />
            <h1 className="text-3xl font-bold tracking-tight">Resume Screener</h1>
          </div>
          <p className="text-center mt-2 text-emerald-100">Send Emails to Candidates</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        {/* Credentials Status Card */}
        <Card className="shadow-lg border-0 animate-fade-in">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-lg">Email Configuration</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loadingCredentials ? (
              <p className="text-muted-foreground">Checking email configuration...</p>
            ) : credentials?.configured ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {credentials.valid ? (
                    <Badge variant="success" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      Invalid credentials
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">From:</span> {credentials.from_name} &lt;{credentials.from_email}&gt;
                </p>
                {!credentials.valid && (
                  <p className="text-sm text-destructive">{credentials.message}</p>
                )}
              </div>
            ) : (
              <Alert variant="warning">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium">Email service not configured</p>
                  <p className="mt-1 text-xs">Set these environment variables:</p>
                  <code className="mt-1 block text-xs bg-muted p-2 rounded">
                    MANDRILL_API_KEY=your_api_key<br />
                    FROM_EMAIL=hr@yourcompany.com<br />
                    FROM_NAME=HR Team
                  </code>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Recipients Selection Card */}
        <Card className="shadow-lg border-0 animate-fade-in">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-lg">Select Recipients</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Option 1: Top N from ranking */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Option 1: Top Ranked Candidates</Label>
              {loadingRanking ? (
                <p className="text-sm text-muted-foreground">Loading ranking data...</p>
              ) : rankingError ? (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between">
                    <span>{rankingError}</span>
                    <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                      Run Job Screening First
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Send to top</span>
                    <Input
                      type="number"
                      min="0"
                      max={rankingData?.total_candidates || 0}
                      value={topN}
                      onChange={(e) => setTopN(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-24"
                    />
                    <span className="text-sm">candidates</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {rankingData?.total_candidates || 0} total candidates • {rankingData?.candidates_with_email || 0} have email addresses
                    {topN > 0 && ` • ${candidatesWithEmails} will receive emails`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Last ranking: {rankingData?.timestamp ? new Date(rankingData.timestamp).toLocaleString() : 'N/A'}
                  </p>
                </div>
              )}
            </div>

            <Separator />

            {/* Option 2: Manual recipients */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Option 2: Manual Recipients</Label>
              <p className="text-xs text-muted-foreground">Add additional recipients who are not in the ranking</p>
              
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={newRecipientEmail}
                  onChange={(e) => setNewRecipientEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addManualRecipient())}
                  className="flex-1"
                />
                <Button variant="outline" onClick={addManualRecipient} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </div>

              {manualRecipients.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Added Recipients ({manualRecipients.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {manualRecipients.map((recipient, index) => (
                      <Badge key={index} variant="secondary" className="gap-1 py-1">
                        {recipient.email}
                        <button
                          onClick={() => removeManualRecipient(index)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Total recipients summary */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="font-medium">Total Recipients:</span>
              <div className="flex items-center gap-2">
                <Badge variant={totalRecipients > 0 ? 'default' : 'secondary'} className="text-base px-3 py-1">
                  {totalRecipients}
                </Badge>
                {totalRecipients > 0 && (
                  <span className="text-sm text-muted-foreground">
                    ({candidatesWithEmails} from ranking + {manualRecipients.length} manual)
                  </span>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Email Compose Card */}
        <Card className="shadow-lg border-0 animate-fade-in">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-lg">Compose Email</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject <span className="text-destructive">*</span></Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g., Exciting Job Opportunity at Aeliasoft"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Content <span className="text-destructive">*</span></Label>
              <ReactQuill
                theme="snow"
                value={htmlContent}
                onChange={setHtmlContent}
                modules={quillModules}
                placeholder="Dear Candidate,&#10;&#10;We are excited to inform you about a new opportunity..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                variant="outline"
                onClick={handlePreview}
                disabled={!htmlContent}
                className="gap-2"
              >
                <Eye className="h-4 w-4" />
                Preview
              </Button>
              <Button 
                onClick={handleSend}
                disabled={sending || !credentials?.valid || totalRecipients === 0}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Send className="h-4 w-4" />
                {sending ? 'Sending...' : `Send to ${totalRecipients} Recipients`}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Send Result */}
        {sendResult && (
          <Card className="shadow-lg border-0 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                📊 Send Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{sendResult.emails_sent}</p>
                  <p className="text-sm text-green-700">Sent Successfully</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{sendResult.emails_failed}</p>
                  <p className="text-sm text-red-700">Failed</p>
                </div>
              </div>
              
              {sendResult.failed?.length > 0 && (
                <div className="space-y-2">
                  <p className="font-medium text-destructive">Failed Emails:</p>
                  <div className="space-y-1">
                    {sendResult.failed.map((item, index) => (
                      <p key={index} className="text-sm">
                        <span className="font-medium">{item.email}</span>: {item.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Preview</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Subject</p>
                <p className="font-medium">{subject}</p>
              </div>
              <Separator />
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Navigation */}
        <div className="flex justify-center pt-4">
          <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
}

export default EmailPage;
